import { forwardRef } from "react";
import {
  Eye,
  Sparkles,
  ScanSearch,
  Target,
  CheckCheck,
  HelpCircle,
} from "lucide-react";
import { Badge, type BadgeProps } from "./Badge";
import { cn } from "@/lib/cn";

/**
 * ChatbotIntentBadge — chatbot pipeline'da aniqlangan **buyer stage**.
 * Manba: docs/architecture/chatbot-ai-pipeline.md § 3.3.a (chatbot-classifier)
 *
 * Bosqichlar:
 *   AWARENESS     — birinchi marta murojaat, mahsulot haqida tushuncha
 *   INTEREST      — qiziqish bildirgan, savollar bermoqda
 *   CONSIDERATION — taqqoslash bosqichi (narx, raqobatchi)
 *   INTENT        — sotib olish niyati aniq (BUYER_INTENT)
 *   PURCHASE      — sotib olgan / yopilgan deal
 */

export type ChatbotBuyerStage =
  | "AWARENESS"
  | "INTEREST"
  | "CONSIDERATION"
  | "INTENT"
  | "PURCHASE";

interface StageMeta {
  label: string;
  icon: typeof Eye;
  cls: string;
  description: string;
}

const META: Record<ChatbotBuyerStage, StageMeta> = {
  AWARENESS: {
    label: "Tanishuv",
    icon: Eye,
    cls: "bg-[var(--intent-info-bg)] text-[var(--intent-info-fg)] border-[var(--intent-info-border)]",
    description: "Mijoz mahsulot bilan endigina tanishmoqda",
  },
  INTEREST: {
    label: "Qiziqish",
    icon: Sparkles,
    cls: "bg-[var(--info-soft)] text-[var(--info-fg)] border-[var(--info-border)]",
    description: "Mijoz savol bermoqda, qiziqish bildirgan",
  },
  CONSIDERATION: {
    label: "Tanlov",
    icon: ScanSearch,
    cls: "bg-[var(--intent-other-bg)] text-[var(--intent-other-fg)] border-[var(--intent-other-border)]",
    description: "Mijoz solishtirmoqda — narx, sifat, alternativalar",
  },
  INTENT: {
    label: "Niyat",
    icon: Target,
    cls: "bg-[var(--warning-soft)] text-[var(--warning-fg)] border-[var(--warning-border)]",
    description: "Sotib olish niyati aniq bildirilgan",
  },
  PURCHASE: {
    label: "Xarid",
    icon: CheckCheck,
    cls: "bg-[var(--success-soft)] text-[var(--success-fg)] border-[var(--success-border)]",
    description: "Mijoz sotib olgan / lead won",
  },
};

const FALLBACK: StageMeta = {
  label: "Aniqlanmagan",
  icon: HelpCircle,
  cls: "bg-[var(--bg-surface)] text-[var(--text-muted)] border-[var(--border-subtle)]",
  description: "Bosqich aniqlanmagan",
};

export interface ChatbotIntentBadgeProps
  extends Omit<BadgeProps, "variant" | "children"> {
  stage: ChatbotBuyerStage | string | null | undefined;
  showIcon?: boolean;
  /** confidence 0..1 — title'ga qo'shiladi */
  confidence?: number;
}

export const ChatbotIntentBadge = forwardRef<
  HTMLSpanElement,
  ChatbotIntentBadgeProps
>(({ stage, showIcon = true, confidence, className, size = "md", ...props }, ref) => {
  const key = (stage ?? "").toString().toUpperCase() as ChatbotBuyerStage;
  const meta = META[key] ?? FALLBACK;
  const Icon = meta.icon;
  const title =
    confidence != null
      ? `${meta.description} · ishonchlilik ${Math.round(confidence * 100)}%`
      : meta.description;
  return (
    <Badge
      ref={ref}
      variant="default"
      size={size}
      className={cn(meta.cls, className)}
      title={title}
      {...props}
    >
      {showIcon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}
      {meta.label}
    </Badge>
  );
});
ChatbotIntentBadge.displayName = "ChatbotIntentBadge";
